/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CHARACTERS, LEVELS, MAZE_TEMPLATES, getMazeForQuestion, shuffleArray, playMoveSound, playCorrectSound, playWrongSound, playLevelUpSound, playStarSound, speakText } from "@/utils/mazeGameData";
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Home, Star, Trophy, Volume2, VolumeX, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { recordGameWon } from "@/utils/playerProfile";
import axios from "axios";

import { logger } from "@/utils/logger";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MazeGame = ({ characterId = "rabbit", onBack }) => {
  const [currentLevel, setCurrentLevel] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [maze, setMaze] = useState(null);
  const [playerPos, setPlayerPos] = useState([0, 0]);
  const [answerMap, setAnswerMap] = useState([]);
  const [feedback, setFeedback] = useState(null); // { type: 'correct'|'wrong', text }
  const [stars, setStars] = useState(0);
  const [soundOn, setSoundOn] = useState(true);
  const [showLevelMap, setShowLevelMap] = useState(false);
  const [showWin, setShowWin] = useState(false);
  const [collectedLetters, setCollectedLetters] = useState([]);
  const [levelStars, setLevelStars] = useState({});
  const [wrongCount, setWrongCount] = useState(0);
  const [saveName, setSaveName] = useState(() => {
    try { const d = localStorage.getItem('alba_game_players'); if (d) { const p = JSON.parse(d); return p[0] || ""; } } catch {} return "";
  });
  const [scoreSaved, setScoreSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const { width, height } = useWindowSize();
  const character = CHARACTERS.find(c => c.id === characterId) || CHARACTERS[2];
  const level = LEVELS[currentLevel];
  const question = level?.questions[currentQuestion];
  const isSpelling = level?.type === "spelling";

  const setupQuestion = useCallback(() => {
    const m = getMazeForQuestion(currentLevel, currentQuestion);
    setMaze(m);
    setPlayerPos([...m.start]);
    setFeedback(null);
    setWrongCount(0);
    setCollectedLetters([]);

    if (isSpelling && question) {
      const shuffled = shuffleArray([...question.letters]);
      setAnswerMap(m.answers.map((pos, i) => ({
        pos,
        value: shuffled[i] || question.letters[i],
        collected: false,
      })));
    } else if (question) {
      const shuffled = shuffleArray([...question.options]);
      setAnswerMap(m.answers.map((pos, i) => ({
        pos,
        value: shuffled[i],
        isCorrect: shuffled[i] === question.correct,
        used: false,
      })));
    }

    if (soundOn && question) {
      setTimeout(() => speakText(question.q), 300);
    }
  }, [currentLevel, currentQuestion, soundOn, isSpelling, question]);

  useEffect(() => { setupQuestion(); }, [setupQuestion]);

  const movePlayer = useCallback((dr, dc) => {
    if (feedback?.type === 'correct' || showLevelMap || showWin) return;
    const nr = playerPos[0] + dr;
    const nc = playerPos[1] + dc;
    if (!maze) return;
    if (nr < 0 || nr >= maze.grid.length || nc < 0 || nc >= maze.grid[0].length) return;
    if (maze.grid[nr][nc] === 1) return;

    if (soundOn) playMoveSound();
    setPlayerPos([nr, nc]);

    // Check if player stepped on an answer
    const ansIdx = answerMap.findIndex(a => a.pos[0] === nr && a.pos[1] === nc && !a.used && !a.collected);
    if (ansIdx === -1) return;

    const ans = answerMap[ansIdx];

    if (isSpelling) {
      const nextLetter = question.letters[collectedLetters.length];
      if (ans.value === nextLetter) {
        if (soundOn) playCorrectSound();
        const newCollected = [...collectedLetters, ans.value];
        setCollectedLetters(newCollected);
        setAnswerMap(prev => prev.map((a, i) => i === ansIdx ? { ...a, collected: true } : a));

        if (newCollected.length === question.letters.length) {
          handleCorrectAnswer();
        }
      } else {
        if (soundOn) { playWrongSound(); setTimeout(() => speakText("Try again, find letter " + nextLetter), 300); }
        setWrongCount(w => w + 1);
        setFeedback({ type: 'wrong', text: `Bukan! Cari huruf ${nextLetter}` });
        setTimeout(() => setFeedback(null), 1500);
      }
    } else {
      if (ans.isCorrect) {
        if (soundOn) playCorrectSound();
        handleCorrectAnswer();
      } else {
        if (soundOn) { playWrongSound(); setTimeout(() => speakText("Try again!"), 300); }
        setWrongCount(w => w + 1);
        setAnswerMap(prev => prev.map((a, i) => i === ansIdx ? { ...a, used: true } : a));
        setFeedback({ type: 'wrong', text: 'Coba lagi ya!' });
        setTimeout(() => setFeedback(null), 1500);
      }
    }
  }, [playerPos, maze, answerMap, feedback, showLevelMap, showWin, isSpelling, collectedLetters, question, soundOn]);

  const handleCorrectAnswer = () => {
    const earnedStars = wrongCount === 0 ? 3 : wrongCount <= 1 ? 2 : 1;
    setStars(s => s + earnedStars);
    if (soundOn) setTimeout(() => playStarSound(), 200);
    setFeedback({ type: 'correct', text: 'Benar! Hebat!' });
    if (soundOn) setTimeout(() => speakText("Correct! Great!"), 200);

    setTimeout(() => {
      setFeedback(null);
      const nextQ = currentQuestion + 1;
      if (nextQ < level.questions.length) {
        setCurrentQuestion(nextQ);
      } else {
        // Level complete
        setLevelStars(prev => ({ ...prev, [currentLevel]: earnedStars }));
        if (currentLevel + 1 < LEVELS.length) {
          if (soundOn) playLevelUpSound();
          setShowLevelMap(true);
        } else {
          setShowWin(true);
          recordGameWon("maze");
          if (soundOn) playLevelUpSound();
        }
      }
    }, 1200);
  };

  const goToNextLevel = () => {
    setShowLevelMap(false);
    setCurrentLevel(prev => prev + 1);
    setCurrentQuestion(0);
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowUp') movePlayer(-1, 0);
      else if (e.key === 'ArrowDown') movePlayer(1, 0);
      else if (e.key === 'ArrowLeft') movePlayer(0, -1);
      else if (e.key === 'ArrowRight') movePlayer(0, 1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [movePlayer]);

  if (!maze || !question) return null;

  const handleSaveMazeScore = async () => {
    if (!saveName.trim() || saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/game/record`, {
        player_name: saveName.trim(),
        mode: "maze",
        duration_seconds: 0,
        score: stars,
        stars: Math.min(3, Math.round(stars / LEVELS.length)),
        level: `${LEVELS.length} Levels`,
        opponent_type: "solo",
      });
      setScoreSaved(true);
    } catch (e) {
      logger.error("Failed to save:", e);
    }
    setSaving(false);
  };

  const cellSize = Math.min(56, Math.floor((Math.min(width, 600) - 32) / 7));
  const cols = maze.grid[0].length;
  const rows = maze.grid.length;

  return (
    <div className="flex flex-col items-center w-full max-w-7xl mx-auto px-2" data-testid="maze-game-container">
      {showWin && <Confetti width={width} height={height} colors={["#FFD700", "#FF6B35", "#4ECDC4", "#FF6B6B"]} recycle={false} numberOfPieces={400} />}

      {/* Top bar */}
      <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full mb-3">
        <div className="glass-card-strong rounded-xl p-3 border border-gold/20">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xl">{level.icon}</span>
              <span className="font-fredoka text-sm sm:text-base" style={{ color: level.color }}>
                Level {level.id}: {level.name}
              </span>
              <span className="text-gold-light/80 text-xs font-fredoka">({level.world})</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 text-white fill-gold" />
                <span className="font-fredoka text-white text-sm">{stars}</span>
              </div>
              <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="border-gold/20 text-gold hover:bg-gold/10 h-8 w-8 p-0" data-testid="maze-sound-toggle">
                {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              </Button>
              <Button onClick={onBack} variant="outline" size="sm" className="border-gold/20 text-pink-300 hover:bg-gold/10 h-8 px-2" data-testid="maze-back-btn">
                <Home className="w-3.5 h-3.5 mr-1" />Menu
              </Button>
            </div>
          </div>
          {/* Question */}
          <div className="rounded-lg p-2.5 border text-center" style={{ borderColor: `${level.color}44`, background: `${level.color}10` }}>
            <p className="font-fredoka text-sm sm:text-base font-bold" style={{ color: level.color }} data-testid="maze-question">
              {question.q}
            </p>
            {isSpelling && (
              <div className="flex justify-center gap-1 mt-1.5">
                {question.letters.map((letter, i) => (
                  <span key={i} className={`font-fredoka text-lg w-8 h-8 flex items-center justify-center rounded border-2 ${
                    i < collectedLetters.length
                      ? 'border-green-400 text-green-400 bg-green-400/10'
                      : i === collectedLetters.length
                      ? 'border-gold/20 text-white animate-pulse'
                      : 'border-gold/20 text-brown-border'
                  }`}>
                    {i < collectedLetters.length ? collectedLetters[i] : '?'}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Feedback Toast */}
      <AnimatePresence>
        {feedback && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10 }}
            className={`mb-2 px-5 py-2 rounded-full font-fredoka text-sm font-bold ${
              feedback.type === 'correct' ? 'bg-green-500/20 text-green-400 border border-green-500/40' : 'bg-red-500/20 text-red-400 border border-red-500/40'
            }`}
            data-testid="maze-feedback"
          >
            {feedback.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Maze Board */}
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="rounded-xl p-3 sm:p-4 border-2 relative"
        style={{
          background: 'rgba(30,20,10,0.85)',
          borderColor: `${level.color}33`,
          boxShadow: `0 0 30px ${level.color}15`,
        }}
      >
        <div
          className="grid relative"
          style={{
            gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
            gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
            gap: '2px',
          }}
          data-testid="maze-grid"
        >
          {maze.grid.map((row, r) =>
            row.map((cell, c) => {
              const isWall = cell === 1;
              const isPlayer = playerPos[0] === r && playerPos[1] === c;
              const answer = answerMap.find(a => a.pos[0] === r && a.pos[1] === c && !a.used && !a.collected);
              const isTargetLetter = isSpelling && answer && question.letters[collectedLetters.length] === answer.value;

              return (
                <div
                  key={`${r}-${c}`}
                  data-testid={`maze-cell-${r}-${c}`}
                  className="relative flex items-center justify-center rounded-md transition-colors"
                  style={{
                    width: cellSize,
                    height: cellSize,
                    background: isWall
                      ? `linear-gradient(135deg, #1a0f00, #251505)`
                      : 'rgba(60,42,18,0.5)',
                    border: isWall ? '1px solid #2a1805' : '1px solid rgba(255,215,0,0.06)',
                    boxShadow: isWall ? 'inset 0 2px 6px rgba(0,0,0,0.5)' : 'none',
                  }}
                >
                  {/* Answer display */}
                  {answer && !isPlayer && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-fredoka font-bold text-lg border-2 ${
                        isTargetLetter ? 'animate-pulse' : ''
                      }`}
                      style={{
                        background: isTargetLetter ? `${level.color}30` : 'rgba(255,255,255,0.1)',
                        borderColor: isTargetLetter ? level.color : 'rgba(255,215,0,0.3)',
                        color: isTargetLetter ? level.color : '#FFD700',
                      }}
                    >
                      {answer.value}
                    </motion.div>
                  )}

                  {/* Player */}
                  {isPlayer && (
                    <motion.div
                      layout
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      className="absolute inset-0 flex items-center justify-center z-10"
                    >
                      <div
                        className="w-11 h-11 rounded-full flex items-center justify-center text-3xl"
                        style={{
                          background: `${character.color}30`,
                          boxShadow: `0 0 15px ${character.color}40`,
                          border: `2px solid ${character.color}80`,
                        }}
                      >
                        {character.emoji}
                      </div>
                    </motion.div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </motion.div>

      {/* Arrow Controls */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mt-4 flex flex-col items-center gap-1"
      >
        <Button
          onClick={() => movePlayer(-1, 0)}
          className="w-14 h-12 bg-gradient-to-r from-yellow-400 to-amber-500/20 hover:bg-gradient-to-r from-yellow-400 to-amber-500/30 text-white border border-gold/20 rounded-xl"
          data-testid="maze-btn-up"
        >
          <ArrowUp className="w-6 h-6" />
        </Button>
        <div className="flex gap-1">
          <Button
            onClick={() => movePlayer(0, -1)}
            className="w-14 h-12 bg-gradient-to-r from-yellow-400 to-amber-500/20 hover:bg-gradient-to-r from-yellow-400 to-amber-500/30 text-white border border-gold/20 rounded-xl"
            data-testid="maze-btn-left"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <Button
            onClick={() => movePlayer(1, 0)}
            className="w-14 h-12 bg-gradient-to-r from-yellow-400 to-amber-500/20 hover:bg-gradient-to-r from-yellow-400 to-amber-500/30 text-white border border-gold/20 rounded-xl"
            data-testid="maze-btn-down"
          >
            <ArrowDown className="w-6 h-6" />
          </Button>
          <Button
            onClick={() => movePlayer(0, 1)}
            className="w-14 h-12 bg-gradient-to-r from-yellow-400 to-amber-500/20 hover:bg-gradient-to-r from-yellow-400 to-amber-500/30 text-white border border-gold/20 rounded-xl"
            data-testid="maze-btn-right"
          >
            <ArrowRight className="w-6 h-6" />
          </Button>
        </div>
      </motion.div>

      {/* Level Map Modal */}
      <AnimatePresence>
        {showLevelMap && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              className="rounded-2xl p-6 border-2 text-center max-w-md w-full"
              style={{ background: 'rgba(30,20,10,0.95)', borderColor: `${level.color}44`, boxShadow: `0 0 40px ${level.color}20` }}
            >
              <h2 className="font-fredoka text-xl sm:text-2xl font-bold mb-2" style={{ color: level.color }}>
                Level {level.id} Selesai!
              </h2>
              <p className="font-fredoka text-gold-light/80 text-sm mb-4">{level.world} berhasil dijelajahi!</p>

              <div className="flex justify-center gap-1 mb-4">
                {[1,2,3].map(s => (
                  <Star key={s} className={`w-8 h-8 ${s <= (levelStars[currentLevel] || 1) ? 'fill-current' : ''}`}
                    style={{ color: s <= (levelStars[currentLevel] || 1) ? '#FFD700' : '#3a2a1a' }} />
                ))}
              </div>

              {/* Mini world map */}
              <div className="flex items-center justify-center gap-2 mb-5 overflow-x-auto py-2">
                {LEVELS.map((lvl, i) => (
                  <div key={lvl.id} className="flex items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg border-2 transition-all ${
                      i < currentLevel + 1 ? 'border-green-400 bg-green-400/10' :
                      i === currentLevel + 1 ? 'border-gold/20 bg-gradient-to-r from-yellow-400 to-amber-500/10 animate-pulse' :
                      'border-gold/20 glass-card opacity-50'
                    }`}>
                      {lvl.icon}
                    </div>
                    {i < LEVELS.length - 1 && (
                      <div className={`w-4 h-0.5 ${i <= currentLevel ? 'bg-green-400' : 'bg-brown-border'}`} />
                    )}
                  </div>
                ))}
              </div>

              <p className="font-fredoka text-gold-light/80 text-sm mb-4">
                Selanjutnya: <span style={{ color: LEVELS[currentLevel + 1]?.color }} className="font-bold">
                  {LEVELS[currentLevel + 1]?.world}
                </span>
              </p>

              <Button onClick={goToNextLevel} className="w-full font-fredoka font-bold text-brown-dark bg-gradient-to-r from-yellow-400 to-amber-500 hover:bg-gradient-to-r from-yellow-400 to-amber-500/90" data-testid="maze-next-level-btn">
                Lanjut Petualangan!
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* All Levels Complete */}
      <AnimatePresence>
        {showWin && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", damping: 15 }}
              className="rounded-2xl p-6 sm:p-8 border-2 text-center max-w-md w-full"
              style={{ background: 'rgba(30,20,10,0.95)', borderColor: '#FFD70044', boxShadow: '0 0 60px rgba(255,215,0,0.2)' }}
            >
              <Trophy className="w-16 h-16 mx-auto mb-3 text-white" />
              <h2 className="font-fredoka text-2xl sm:text-3xl font-bold text-white mb-2">
                Petualangan Selesai!
              </h2>
              <p className="font-fredoka text-gold-light/80 mb-3">Semua labirin berhasil ditaklukkan!</p>
              <div className="flex items-center justify-center gap-2 mb-5">
                <Star className="w-6 h-6 text-white fill-gold" />
                <span className="font-fredoka text-2xl text-white">{stars}</span>
                <span className="font-fredoka text-gold-light/80 text-sm">bintang terkumpul</span>
              </div>
              <div className="flex flex-col gap-2">
                {!scoreSaved ? (
                  <div className="flex gap-2 mb-1">
                    <input
                      data-testid="maze-save-name-input"
                      type="text"
                      value={saveName}
                      onChange={(e) => setSaveName(e.target.value)}
                      placeholder="Nama kamu..."
                      maxLength={50}
                      className="flex-1 glass-card border border-gold/20 rounded-lg px-3 py-2 text-white font-fredoka text-sm focus:outline-none focus:border-gold/20"
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveMazeScore()}
                    />
                    <Button onClick={handleSaveMazeScore} disabled={!saveName.trim() || saving}
                      className="font-fredoka font-bold text-brown-dark px-4" style={{ background: '#4ade80' }}
                      data-testid="maze-save-score-btn">
                      <Save className="w-4 h-4 mr-1" />{saving ? '...' : 'Simpan'}
                    </Button>
                  </div>
                ) : (
                  <p className="text-green-400 font-fredoka text-sm mb-1" data-testid="maze-score-saved">Skor tersimpan!</p>
                )}
                <Button onClick={() => { setCurrentLevel(0); setCurrentQuestion(0); setStars(0); setLevelStars({}); setShowWin(false); setSaveName(""); setScoreSaved(false); }}
                  className="w-full font-fredoka font-bold text-brown-dark bg-gradient-to-r from-yellow-400 to-amber-500 hover:bg-gradient-to-r from-yellow-400 to-amber-500/90" data-testid="maze-replay-btn">
                  Main Lagi dari Awal
                </Button>
                <Button onClick={onBack} variant="outline" className="w-full border-gold/20 text-pink-300 hover:bg-gold/10 font-fredoka" data-testid="maze-finish-btn">
                  <Home className="w-4 h-4 mr-2" />Kembali ke Menu
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MazeGame;
